/**
 * SessionSettingsDropdown Component
 *
 * Inline dropdown panel for session analysis settings.
 * Positioned absolutely below the settings button.
 */

import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SessionSettings, SaveStatus } from '@/hooks/useSessionSettings';

interface SessionSettingsDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  settings: SessionSettings;
  saveStatus: SaveStatus;
  hasUnsavedChanges: boolean;
  onUpdateSettings: (updates: Partial<SessionSettings>) => void;
  onSaveSettings: (settings?: SessionSettings) => Promise<void>;
  dialogRef: React.RefObject<HTMLDivElement>;
}

export const SessionSettingsDropdown: React.FC<SessionSettingsDropdownProps> = ({
  isOpen,
  onClose,
  settings,
  saveStatus,
  hasUnsavedChanges,
  onUpdateSettings,
  onSaveSettings,
  dialogRef,
}) => {
  const { t } = useTranslation();
  const [yoloInputValue, setYoloInputValue] = useState<string>(settings.yolo_img_size.toString());

  // Sync YOLO input value when settings change
  useEffect(() => {
    setYoloInputValue(settings.yolo_img_size.toString());
  }, [settings.yolo_img_size]);

  // Helper function to round to nearest 64-divisible value
  const roundToNearest64 = (value: number): number => {
    return Math.round(value / 64) * 64;
  };

  // Utility function to get gradient color for sliders
  const getSliderGradient = (value: number, min = 0.0, max = 1.0) => {
    const normalizedValue = Math.max(0, Math.min(1, (value - min) / (max - min)));
    return {
      background: `linear-gradient(90deg, #ff0000 0%, #ffff00 50%, #00ff00 100%)`,
      "--slider-value": `${normalizedValue * 100}%`,
    } as React.CSSProperties;
  };

  const handleSettingChange = (updates: Partial<SessionSettings>) => {
    onUpdateSettings(updates);
  };

  if (!isOpen) return null;

  return (
    <div
      ref={dialogRef}
      className="absolute z-50 bg-white/100 backdrop-blur-sm rounded-lg shadow-2xl border border-gray-200 max-w-md w-80"
      style={{
        position: "absolute",
        top: "100%",
        right: "0",
        marginTop: "8px",
      }}
    >
      <style>
        {`
          input[type="range"] {
            -webkit-appearance: none;
            appearance: none;
            height: 8px;
            border-radius: 4px;
            outline: none;
          }
          input[type="range"]::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: #ffffff;
            cursor: pointer;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            border: 2px solid #4F46E5;
          }
          input[type="range"]::-moz-range-thumb {
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: #ffffff;
            cursor: pointer;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            border: 2px solid #4F46E5;
          }
        `}
      </style>

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <h2 className="text-lg font-medium text-gray-900">
          {t("session_details.analysis_settings")}
        </h2>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <X className="h-4 w-4 text-gray-500" />
        </button>
      </div>

      {/* Content */}
      <div className="p-4 space-y-6 max-h-[60vh] overflow-y-auto">
        {/* YOLO Detection Settings */}
        <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
            Detection
          </h4>

          {/* Confidence Threshold */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("session_details.settings_labels.confidence_threshold")}
            </label>
            <div className="relative">
              <input
                type="range"
                min="0.0"
                max="1.0"
                step="0.01"
                value={settings.conf_threshold}
                onChange={(e) => {
                  const newValue = parseFloat(e.target.value);
                  handleSettingChange({
                    conf_threshold: newValue,
                    confidence_threshold: newValue,
                  });
                }}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                style={getSliderGradient(settings.conf_threshold)}
              />
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {(settings.conf_threshold * 100).toFixed(1)}%
            </div>
          </div>

          {/* IoU Threshold */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("session_details.settings_labels.iou_threshold")}
            </label>
            <div className="relative">
              <input
                type="range"
                min="0.0"
                max="1.0"
                step="0.01"
                value={settings.iou_threshold}
                onChange={(e) => {
                  const newValue = parseFloat(e.target.value);
                  handleSettingChange({ iou_threshold: newValue });
                }}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                style={getSliderGradient(settings.iou_threshold)}
              />
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {(settings.iou_threshold * 100).toFixed(1)}%
            </div>
          </div>

          {/* Detection Image Size */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("session_details.settings_labels.detection_image_size")}
            </label>
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => {
                  const newValue = Math.max(640, settings.yolo_img_size - 64);
                  handleSettingChange({ yolo_img_size: newValue });
                  setYoloInputValue(newValue.toString());
                }}
                disabled={settings.yolo_img_size <= 640}
                className="flex-shrink-0 w-8 h-8 flex items-center justify-center border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                -
              </button>
              <div className="flex-1 text-center">
                <input
                  type="text"
                  value={yoloInputValue}
                  onChange={(e) => {
                    if (/^\d*$/.test(e.target.value)) {
                      setYoloInputValue(e.target.value);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const inputNumber = parseInt(yoloInputValue);
                      if (isNaN(inputNumber)) {
                        setYoloInputValue(settings.yolo_img_size.toString());
                        return;
                      }
                      const clampedValue = Math.min(Math.max(inputNumber, 640), 6400);
                      const roundedValue = roundToNearest64(clampedValue);
                      handleSettingChange({ yolo_img_size: roundedValue });
                      setYoloInputValue(roundedValue.toString());
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                  onBlur={() => setYoloInputValue(settings.yolo_img_size.toString())}
                  onFocus={() => (document.activeElement as HTMLInputElement)?.select()}
                  className="w-24 px-2 py-1 text-center border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter size"
                />
                <div className="text-xs text-gray-500 mt-1">
                  {t("session_details.range_validation.pixels")}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  const newValue = Math.min(6400, settings.yolo_img_size + 64);
                  handleSettingChange({ yolo_img_size: newValue });
                  setYoloInputValue(newValue.toString());
                }}
                disabled={settings.yolo_img_size >= 6400}
                className="flex-shrink-0 w-8 h-8 flex items-center justify-center border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                +
              </button>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Press Enter to confirm (rounds to nearest 64)
            </div>
          </div>

          {/* Disable Manual Detection Discard Toggle */}
          <div>
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Disable Manual Detection Discard
              </label>
              <button
                type="button"
                onClick={async () => {
                  const newValue = !settings.disable_manual_detection_discard;
                  handleSettingChange({ disable_manual_detection_discard: newValue });
                  await onSaveSettings({ ...settings, disable_manual_detection_discard: newValue });
                }}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  settings.disable_manual_detection_discard ? "bg-primary-600" : "bg-gray-200"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${
                    settings.disable_manual_detection_discard ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {settings.disable_manual_detection_discard
                ? "Manual detections will never be discarded regardless of quality/confidence thresholds"
                : "Manual detections that fail quality or confidence thresholds will be discarded"}
            </div>
          </div>
        </div>

        {/* ArcFace Matching Settings */}
        <div className="space-y-4 p-4 bg-purple-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
            <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
            Recognition
          </h4>

          {/* Cutoff Threshold */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("session_details.settings_labels.cutoff_threshold")}
            </label>
            <div className="relative">
              <input
                type="range"
                min="0.0"
                max="1.0"
                step="0.01"
                value={settings.cutoff_threshold}
                onChange={(e) => {
                  const newValue = parseFloat(e.target.value);
                  handleSettingChange({ cutoff_threshold: newValue });
                }}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                style={getSliderGradient(settings.cutoff_threshold)}
              />
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {(settings.cutoff_threshold * 100).toFixed(1)}%
            </div>
          </div>

          {/* Top Distances */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Top Distances
            </label>
            <div className="relative">
              <input
                type="range"
                min="5"
                max="30"
                step="1"
                value={settings.top_distances}
                onChange={(e) => {
                  const newValue = parseInt(e.target.value);
                  handleSettingChange({ top_distances: newValue });
                }}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                style={getSliderGradient(settings.top_distances, 5, 30)}
              />
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {settings.top_distances} distances
            </div>
          </div>

          {/* Major Votes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Major Votes
            </label>
            <div className="relative">
              <input
                type="range"
                min="1"
                max="10"
                step="1"
                value={settings.major_votes}
                onChange={(e) => {
                  const newValue = parseInt(e.target.value);
                  handleSettingChange({ major_votes: newValue });
                }}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                style={getSliderGradient(settings.major_votes, 1, 10)}
              />
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {settings.major_votes} votes required
            </div>
          </div>
        </div>

        {/* General Settings */}
        <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
            <span className="w-2 h-2 bg-gray-500 rounded-full"></span>
            General Settings
          </h4>

          {/* Show Uncertain Detections Toggle */}
          <div>
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Show Uncertain Detections
              </label>
              <button
                type="button"
                onClick={async () => {
                  const newValue = !settings.show_uncertain_detections;
                  handleSettingChange({ show_uncertain_detections: newValue });
                  await onSaveSettings({ ...settings, show_uncertain_detections: newValue });
                }}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  settings.show_uncertain_detections ? "bg-primary-600" : "bg-gray-200"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${
                    settings.show_uncertain_detections ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {settings.show_uncertain_detections
                ? "Detections that fail thresholds will be shown with dashed borders"
                : "Detections that fail thresholds will be discarded"}
            </div>
          </div>

          {/* Top Matches Display */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("session_details.settings_labels.top_matches_display_k")}
            </label>
            <select
              value={settings.topK}
              onChange={(e) => {
                const newValue = parseInt(e.target.value);
                handleSettingChange({ topK: newValue });
              }}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value={3}>3</option>
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
            </select>
          </div>
        </div>

        {/* Quality Assessment (Refiner) */}
        <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
            <span className="w-2 h-2 bg-gray-500 rounded-full"></span>
            Quality Assessment
          </h4>

          {/* Enable Refiner Toggle */}
          <div>
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Enable Quality Assessment
              </label>
              <button
                type="button"
                onClick={async () => {
                  const newValue = !settings.refiner_enabled;
                  handleSettingChange({ refiner_enabled: newValue });
                  await onSaveSettings({ ...settings, refiner_enabled: newValue });
                }}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  settings.refiner_enabled ? "bg-primary-600" : "bg-gray-200"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${
                    settings.refiner_enabled ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {settings.refiner_enabled
                ? "AI will assess the quality of detected crops"
                : "Quality assessment is disabled"}
            </div>
          </div>

          {/* Quality Threshold */}
          <div className={settings.refiner_enabled ? "" : "opacity-50 pointer-events-none"}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quality Threshold
            </label>
            <div className="relative">
              <input
                type="range"
                min="0.0"
                max="1.0"
                step="0.01"
                value={settings.refiner_threshold}
                onChange={(e) => {
                  const value = parseFloat(e.target.value);
                  handleSettingChange({ refiner_threshold: value });
                }}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                style={getSliderGradient(settings.refiner_threshold)}
              />
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {(settings.refiner_threshold * 100).toFixed(1)}% - Crops with quality below this
              threshold will be treated as uncertain
            </div>
          </div>

          {/* Quality Assessment Trigger */}
          <div className={settings.refiner_enabled ? "" : "opacity-50 pointer-events-none"}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quality Assessment Trigger
            </label>
            <div className="relative">
              <input
                type="range"
                min="0.0"
                max="1.0"
                step="0.01"
                value={settings.refiner_trigger_threshold}
                onChange={(e) => {
                  const value = parseFloat(e.target.value);
                  handleSettingChange({ refiner_trigger_threshold: value });
                }}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                style={getSliderGradient(settings.refiner_trigger_threshold)}
              />
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {(settings.refiner_trigger_threshold * 100).toFixed(1)}% - Run quality assessment
              only on detections below this confidence
            </div>
          </div>
        </div>
      </div>

      {/* Footer - Auto-save status */}
      <div className="p-4 border-t border-gray-100 text-center">
        <div className="text-sm text-gray-600">
          {saveStatus === "idle" && !hasUnsavedChanges && "Settings saved automatically on close"}
          {saveStatus === "idle" && hasUnsavedChanges && (
            <span className="text-orange-600">
              Changes will be saved when you close this dialog
            </span>
          )}
          {saveStatus === "saving" && (
            <span className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
              Saving changes...
            </span>
          )}
          {saveStatus === "saved" && (
            <span className="flex items-center justify-center gap-2 text-green-600">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              Settings saved successfully
            </span>
          )}
          {saveStatus === "error" && (
            <span className="flex items-center justify-center gap-2 text-red-600">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              Failed to save settings
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionSettingsDropdown;
